import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, Users, Trash2, Search, ShieldCheck } from "lucide-react";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "-";

export default function AdminAccounts() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null); // user to clear
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => load(), [load]);

  const confirmClear = async () => {
    setClearing(true);
    try {
      const { data } = await api.delete(`/admin/users/${target.id}/bets`);
      toast.success(`Cleared ${data.deleted} bet${data.deleted === 1 ? "" : "s"} for ${target.email}`);
      setTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  const filtered = users.filter(
    (u) =>
      !q.trim() ||
      u.email.toLowerCase().includes(q.toLowerCase()) ||
      (u.name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
          <Users className="h-8 w-8 text-primary" /> Accounts
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Every signed-up account. You can wipe a user's bet history, but you can't see their
          passwords or the contents of their bets.
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or email"
          data-testid="account-search"
          className="rounded-full h-10 pl-9"
        />
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">{filtered.length} account{filtered.length === 1 ? "" : "s"}</div>
          <div className="space-y-2" data-testid="accounts-list">
            {filtered.map((u) => (
              <div
                key={u.id}
                className="glass rounded-2xl p-4 flex items-center gap-4"
                data-testid="account-row"
              >
                <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center shrink-0 font-display font-bold text-sm">
                  {(u.name || u.email || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{u.name || "-"}</span>
                    {u.role === "admin" && (
                      <Badge className="rounded-full text-[10px] border-0 bg-primary/15 text-primary gap-1">
                        <ShieldCheck className="h-3 w-3" /> admin
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <div className="hidden sm:block text-xs text-muted-foreground text-right shrink-0">
                  Joined {fmtDate(u.created_at)}
                </div>
                <div className="text-center shrink-0 w-16">
                  <div className="font-display font-bold" data-testid="account-bet-count">{u.bet_count}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">bets</div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={u.bet_count === 0}
                  onClick={() => setTarget(u)}
                  data-testid="clear-user-bets-btn"
                  className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/10 disabled:opacity-40 shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Clear bets
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="admin-clear-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this user's bet history?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all {target?.bet_count} bet{target?.bet_count === 1 ? "" : "s"} for{" "}
              <b>{target?.email}</b>. Their account and settings stay. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmClear}
              disabled={clearing}
              data-testid="confirm-admin-clear"
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear bets"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
